import { NgClass } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AgGridAngular } from 'ag-grid-angular';
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';

import { AGGridFormatterService } from '../../../services/ag-grid/formatter-service';
import { RecourseBookingStore } from '../../../services/booking/recourse-booking-store';
import { UserService } from '../../../services/user-service/user.service';
import { AgGridService } from '../../../shared/ag-grid/ag-grid.service';
import { BookingStateEnum } from '../../../shared/enums/booking-state-enum';
import { BookingTypeEnum } from '../../../shared/enums/booking-type-enum';
import { RecourseBookingModel } from '../model/recourse-booking-model';
import { RecourseBookingButtonCellRenderer } from '../renderer/recourse-booking-button-cell-renderer.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'reference-bookings',
  templateUrl: './reference-bookings.component.html',
  styleUrls: ['./reference-bookings.component.scss'],
  imports: [AgGridAngular, TranslatePipe, NgClass],
})
export class ReferenceBookingsComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly agGridService = inject(AgGridService);
  private readonly recourseBookingStore = inject(RecourseBookingStore);
  private readonly userService = inject(UserService);
  private readonly agGridFormatterService = inject(AGGridFormatterService);

  @Input()
  public referenceBookings: RecourseBookingModel[];

  public gridOptions: GridOptions;
  public columnDefs: any;
  public defaultColDef: any;
  public frameworkComponents: any;
  public widthToAlignTotalAmount: number;

  private gridApi: GridApi;
  private selectedBooking: RecourseBookingModel;
  private referencedBookingId: number;
  private totalReferencedAmountNumber: number;
  private tmpParam: any;

  public parentFormDetailAddEditChanged = false;

  public totalReferencedAmount = '';
  public referenceTableSumHidden = true;

  public ngOnInit(): void {
    this.initializeView();
  }

  public initializeView(): void {
    this.selectedBooking = this.recourseBookingStore.selectedRecourseBooking;
    this.referencedBookingId = this.selectedBooking.referencedBookingId ?? this.selectedBooking.pkBookingId;
    this.frameworkComponents = {
      recourseBookingButtonCellRenderer: RecourseBookingButtonCellRenderer,
    };
    this.setupGrid();
  }

  private setupGrid(): void {
    this.gridOptions = this.agGridService.newDefaultGridOptions();
    this.gridOptions.getLocaleText = this.agGridService.translate(this.userService, this.translateService);
    this.gridOptions.overlayComponentParams = {
      noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
    };
    this.gridOptions.pagination = false;

    this.gridOptions.context = {
      selectedBooking: this.recourseBookingStore.selectedRecourseBooking,
      cancelledState: BookingStateEnum.CANCELLED,
      parentReferenceBookingsComponent: this,
      credit: BookingTypeEnum.CREDIT,
    };

    this.gridOptions.suppressCellFocus = true;
    const referenceMarker: string = this.translateService.instant('REFERENCE');
    this.defaultColDef = {
      sortable: false,
      suppressHeaderMenuButton: true,
      floatingFilter: false,
    };
    this.columnDefs = [
      {
        colId: 'id',
        headerName: this.translateService.instant('REFERENCED_BOOKING_ID'),
        field: 'pkBookingId',
        width: 80,
      },
      {
        colId: 'amount',
        headerName: this.translateService.instant('REFERENCED_BOOKING_AMOUNT'),
        width: 80,
        type: 'rightAligned',
        valueGetter: this.agGridService.getEurAmountWithBookingTypeCheck(),
        valueFormatter: this.agGridFormatterService.currencyFormatter(this.userService),
      },
      {
        headerName: this.translateService.instant('REFERENCED_BOOKING_DUE_DATE'),
        width: 80,
        valueGetter: this.agGridService.getDueDate(),
        valueFormatter: this.agGridFormatterService.dateFormatter,
      },
      {
        headerName: this.translateService.instant('REFERENCED_BOOKING_TYPE'),
        valueGetter: this.agGridService.translateBookingType(this.translateService),
        width: 80,
      },
      {
        headerName: this.translateService.instant('REFERENCED_BOOKING_STATUS'),
        valueGetter: this.agGridService.getBookingStateName(this.translateService),
        width: 80,
      },
      {
        headerName: '',
        field: '',
        width: 80,
        cellRendererParams: {
          referencedBookingId: this.referencedBookingId,
        },
        cellRenderer: params => (params.data.pkBookingId === params.referencedBookingId ? referenceMarker : ''),
      },
      {
        headerName: '',
        headerClass: ['ag-header-last-cell'],
        field: 'value',
        cellRenderer: 'recourseBookingButtonCellRenderer',
        filter: false,
        menuTabs: [],
        unSortIcon: false,
        width: 80,
      },
    ];
  }

  public onGridSizeChanged(params): void {
    params.api.sizeColumnsToFit();
    this.widthToAlignTotalAmount = params.api.getColumn('id').actualWidth + params.api.getColumn('amount').actualWidth;
  }

  public onGridReady(event: GridReadyEvent): void {
    this.tmpParam = event;
    this.gridApi = event.api;

    this.gridOptions.overlayNoRowsTemplate = `<span>${this.translateService.instant(
      'NO_REFERENCED_BOOKINGS_AVAILABLE',
    )}</span>`;

    this.gridOptions.rowClass = 'white-row';
    this.gridOptions.rowClassRules = {
      'gray-characters': 'data.bookingStateId === ctx.cancelledState',
      'higlighted-row': 'data.pkBookingId === ctx.selectedBooking.pkBookingId',
      'red-characters': 'data.bookingType === ctx.credit && data.bookingStateId !== ctx.cancelledState',
    };
    this.gridApi.sizeColumnsToFit();
    event.api.setGridOption('rowData', this.referenceBookings);

    if (this.referenceBookings && this.referenceBookings.length > 0) {
      const totalReferencedAmountPom = this.referenceBookings[0].totalReferencedAmount;
      this.totalReferencedAmountNumber = this.referenceBookings[0].totalReferencedAmount;
      this.totalReferencedAmount =
        totalReferencedAmountPom == null
          ? ''
          : this.currencyFormatter(totalReferencedAmountPom, 'EUR', this.userService);
      this.referenceTableSumHidden = false;
    } else {
      this.referenceTableSumHidden = true;
    }
  }

  public getParentFormChanged(): boolean {
    return this.parentFormDetailAddEditChanged;
  }

  public negativeTotalAmount(): boolean {
    return this.totalReferencedAmountNumber < 0;
  }

  private currencyFormatter(totalReferencedAmount: number, currencyKey: string, userService: UserService): string {
    return new Intl.NumberFormat(userService.userLanguage, {
      style: 'currency',
      currency: currencyKey,
    }).format(totalReferencedAmount);
  }

  public refreshTable(newReferenceBookings: RecourseBookingModel[]): void {
    this.referenceBookings = newReferenceBookings;
    this.onGridReady(this.tmpParam);
  }
}
